<script setup>
import Icon from 'vue-elements/icon/Icon.vue';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import Sidebar from '../components/Sidebar.vue';
import UspPart from '../components/form/UspPart.vue';
import PersonalDataPart from '../components/form/PersonalDataPart.vue';
import DeliveryAddressPart from '../components/form/DeliveryAddressPart.vue';
import ConfirmPart from '../components/form/ConfirmPart.vue';
import DisclaimerPart from '../components/form/DisclaimerPart.vue';
import OrderPricing from '../components/EnergyProductPricing.vue';
import StartDatePart from '../components/form/StartDatePart.vue';
import ProductFlag from '../components/ProductFlag.vue';
import ProductUsps from '../components/ProductUsps.vue';
import { useOrderStore } from '../stores/orderStore.js';
import { logAnalyticsEvent } from '../../../../assets/js/helpers/bananas.js';
import FormattedEuro from '../../../vue-elements/product-card/FormattedEuro.vue';

const orderStore = useOrderStore();
const router = useRouter();

const showProductInfo = ref(false);
const toggleIcon = computed(() => (showProductInfo.value ? 'chevron-up' : 'info-circle'));
const tryScrollToError = (errors) => {
  Object.keys(errors)
    .every((key) => {
      const element = document.getElementById(key);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        element.focus();
        return false;
      }
      return true;
    });
};
const submit = () => {
  if (!orderStore.loading) {
    orderStore.submitOrder(router)
      .catch((errors) => {
        tryScrollToError(errors);
      });
  }
};

const startedTyping = ref(false);

watch(orderStore, () => {
  if (!startedTyping.value) {
    startedTyping.value = true;
    logAnalyticsEvent('compare-energy.order-form-started-typing', orderStore.analyticsProduct);
  }
}, { deep: true });

onMounted(() => {
  logAnalyticsEvent('compare-energy.order-form-load', orderStore.analyticsProduct);
});

// If the form was loaded from order flow, update the '<- Terug' button to go back to the order flow.
const emits = defineEmits(['update:orderFlowSignup']);

defineProps({
  orderFlowSignup: {
    type: Boolean,
    default: false,
  },
});

onMounted(() => {
  if (orderStore.orderFlowSignup) {
    emits('update:orderFlowSignup', orderStore.orderFlowSignup);
  }
});
</script>

<template>
  <div class="energy-flow-container energy-flow-container form-container">
    <div class="energy-flow-view form">
      <section class="mobile-product-info">
        <div v-auto-animate class="">
          <div class="product-info-toggle" @click="showProductInfo = !showProductInfo">
            <div class="top-bar">
              <img class="provider-logo" :src="orderStore.product.provider_logo" :alt="orderStore.product.provider_name"
                height="52">

              <div>
                <FormattedEuro class="summary-price" :price="orderStore.product.month_price" appendage="per maand" />
                <div v-if="orderStore.product.total_discount > 0" class="text-small discount"> incl.
                  {{ toCurrencyShortEur(orderStore.product.total_discount) }} korting </div>
                <span v-else-if="orderStore.product.tariff_type === 'dynamic'" class="text-small contract">
                  Dynamische tarieven
                </span>
                <span v-else-if="orderStore.product.tariff_type === 'variable'" class="text-small contract">
                  Variabele tarieven
                </span>
                <span v-else class="text-small contract"> {{ orderStore.product.contract_duration_text }} vast contract
                </span>
              </div>
              <div class="link-element mobile-product-toggle" role="button">
                <span class="label">Details</span>
                <icon :icon="toggleIcon" size="sm" />
              </div>
            </div>
          </div>


          <section v-if="showProductInfo" class="product-info-container mobile">
            <!-- <product-flag v-show="orderStore.product.rank === 1" /> -->
            <product-usps :product="orderStore.product" />

            <order-pricing :vat-disclaimer="true" />
            <div class="close-product-info">

              <span class="link-element " @click="showProductInfo = false">
                <span class="toggle">
                  Inklappen
                  <icon icon="chevron-up" size="sm" />
                </span>
              </span>
            </div>

          </section>
        </div>
      </section>

      <div class="energy-flow-parts-container">
        <usp-part />

        <personal-data-part />

        <delivery-address-part />

        <confirm-part />

        <start-date-part />

        <disclaimer-part @submit="submit()" />
      </div>
      <sidebar :chat="true" :trophies="true">
        <template #content>
          <product-flag v-show="orderStore.product.rank === 1" class="sidebar-flag" />
          <section class="product-overview desktop">
            <div class="provider-info">
              <h5 class="heading">
                Jouw keuze
              </h5>
              <img class="provider-logo" :src="orderStore.product.provider_logo"
                :alt="orderStore.product.provider_name">
            </div>
            <product-usps :product="orderStore.product" />
            <order-pricing />
          </section>

          <div class="disclaimer desktop">
            Deze tarieven zijn inclusief {{ orderStore.product.price_calculation.vat_percentage }}% btw.
            <br>
            Het maandelijkse bedrag kun je later veranderen bij {{ orderStore.product.provider_name }}.
          </div>
        </template>
      </sidebar>
    </div>
  </div>
</template>

<style lang="scss">
@use 'assets/scss/all' as *;

.simple-footer {
  background-color: #f3fdff;
}

.form-container {

  .text-input input,
  .big-radio-input label,
  .date-input input,
  .select-input select.placeholder-shown {
    border-color: #82c5d7;
    font-weight: 500;

    &::placeholder {
      color: #a6d1e1;
    }
  }

  .select-input.button select:not(.placeholder-shown).show-valid:valid {
    color: #171717 !important;
  }

  .select-input select.placeholder-shown {
    color: #a6d1e1 !important;
    font-weight: 500 !important;
  }

  label.field-label {
    margin-left: 4px;
    margin-bottom: 6px;
  }

  .field-error {
    margin-left: 4px;
  }

  .tooltip .toggle .icon-element {
    background: $text-subtle;
  }

  .asap-notice {
    .tooltip .toggle .icon-element {
      background-color: #15A35F !important;
    }
  }

}

.form-container .energy-flow-view {
  @include breakpoint-down(xl) {
    .flow-part {
      border-top: 1px solid #c4e3ed;
      padding-top: 28px;
      margin-top: -28px;
    }

    #UspPart .flow-part,
    #DisclaimerPart .flow-part {
      border-top: 0;
    }
  }

  @include breakpoint-down(md) {
    .flow-part {
      padding-top: 24px;
      margin-top: -24px;
    }
  }
}

.mobile-product-info {
  .product-info-toggle {
    display: grid;
  }

  .formatted-price.summary-price {
    .cent {
      font-weight: 600;
      font-size: 15px;
    }

    .euro {
      font-weight: 600;
      font-size: 18px;
    }

    .appendage {
      font-size: 15px;
    }
  }

  .text-small {
    font-size: 15px;
  }

  .contract {
    color: $text-subtle;
  }

  .discount {
    display: inline-flex;
    font-weight: 600;
    color: $text-positive;
    border-radius: 4px;
  }
}
</style>

<style lang="scss" scoped>
@use 'assets/scss/_variables/colors';
@use 'assets/scss/_helpers/display';
@use 'assets/scss/_helpers/typography';
@use 'assets/scss/_variables/shadows';
@use 'assets/scss/_helpers/breakpoint';
@use '../style/helpers';

.mobile-product-info {
  @include shadows.hover();

  padding: 16px;
  background: colors.$element-base;
  border-radius: 0px 0px 15px 15px;
  border: 1px solid colors.$border-base;
  position: relative;

  @include breakpoint.up(xl) {
    display: none;
  }

  +.energy-flow-parts-container>.part-container:first-child {
    @include breakpoint.down(xl) {
      padding-top: 32px;
    }
  }
}


.energy-flow-container {
  padding-top: 0;
}

.flow-part-container {
  @include breakpoint.up(md) {
    padding-right: 0;
    // margin-right: 48px;
  }
}

.energy-flow-parts-container {
  background: #fff;
  padding-top: 0px;

  @include breakpoint.up(xl) {
    padding-top: 48px
  }
}

.sidebar {
  padding: 0 24px;
  padding-top: 32px;

  @include breakpoint.up(xl) {
    padding: 0;
    padding-top: 48px;
  }

  .product-flag {
    display: none;

    @include breakpoint.up(md) {
      display: unset;
    }
  }
}

.product-overview {
  background-color: colors.$element-base;
  border: 1px solid colors.$border-base;
  border-radius: 15px;

  &.desktop {
    display: none;

    @include breakpoint.up(md) {
      display: block;
    }
  }
}

.provider-info {
  padding: 16px;
}

.provider-logo {
  max-width: 80px;

  @include breakpoint.up(md) {
    max-width: 96px;
  }
}

.product-info-toggle {
  cursor: pointer;

  .top-bar {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
  }


  .price-preview {
    @include display.flex-row-start;
    gap: 4px;

    .price {
      @include typography.content-short-strong;
    }

    .label {
      @include typography.content-short;
    }
  }




  .product-usps {
    @include display.flex-col-start;

    font-size: 16px;
    gap: 4px;
  }

  .product-usp {
    @include display.flex-row-start;
    gap: 4px;

    .icon-element {
      background-color: colors.$text-positive;
    }
  }
}

.product-info-container.mobile {
  background-color: colors.$element-base;
}

.close-product-info {
  @include display.flex-row-center;
  margin-top: 12px;
}

.sidebar-flag.product-flag {
  display: inline-block;
  margin-left: 16px;
  border-radius: 5px 5px 0 0;
  border-bottom: 0;
}

.disclaimer {
  color: colors.$text-subtle;
  margin: 16px 16px 48px;
  font-size: 0.9em;
  line-height: 1.3em;

  &.desktop {
    display: none;

    @include breakpoint.up(md) {
      display: block;
    }
  }

}

.product-overview.desktop {
  position: relative;
  border-radius: 15px;
  overflow: hidden;

  .provider-info {
    @include display.flex-row-between;
  }

  .product-usps {
    padding: 0 16px;
  }
}

.input-datefield-element {
  @include helpers.field-group-spacing-top();
}
</style>
